import { useEffect, useState } from "react";
import { api } from "../api";
import { KEY_FACT_EMOJI, type CaseRecord, type KeyFact } from "../types";
import Tooltip from "./ui/Tooltip";
import AiDisclaimer from "./ui/AiDisclaimer";
import { formatDate } from "../lib/formatDate";

// Persistent, collapsible panel that surfaces AI-extracted key facts
// from every HIGH-significance record in the case. Cached on the case
// row (cases.key_facts JSON) so opening the case doesn't trigger an AI
// call — only "Refresh" does.

interface Props {
  caseId: string;
  // Passed through so the "Source: YYYY-MM-DD" link on each fact can
  // resolve to an actual record id and scroll there. Optional for
  // back-compat with older call sites.
  records?: CaseRecord[];
  onNavigateToRecord?: (recordId: string) => void;
}

function resolveSourceRecord(
  source: string | undefined,
  records: CaseRecord[] | undefined,
): CaseRecord | null {
  if (!source || !records) return null;
  const exact = records.find((r) => r.record_date === source);
  if (exact) return exact;
  return records.find((r) => (r.record_date ?? "").startsWith(source)) ?? null;
}

function formatSourceLabel(source: string): string {
  return formatDate(source, "short") || source;
}

export default function KeyFactsPanel({
  caseId,
  records,
  onNavigateToRecord,
}: Props) {
  const [facts, setFacts] = useState<KeyFact[] | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getKeyFacts(caseId);
        if (cancelled) return;
        setFacts(res.key_facts);
        setUpdatedAt(res.updated_at);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  async function refresh() {
    setRefreshing(true);
    setError(null);
    try {
      const res = await api.refreshKeyFacts(caseId);
      setFacts(res.key_facts);
      setUpdatedAt(res.updated_at);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRefreshing(false);
    }
  }

  const hasFacts = facts !== null && facts.length > 0;

  return (
    <section className="key-facts-panel">
      <header
        className="key-facts-head"
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <span className="key-facts-caret" aria-hidden>
          {open ? "▾" : "▸"}
        </span>
        <h3>Key facts</h3>
        {updatedAt && (
          <span className="key-facts-stamp muted">
            Updated {formatDate(updatedAt, "short")}
          </span>
        )}
        <div
          className="key-facts-actions"
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip
            title={
              hasFacts
                ? "Regenerate key facts from current records"
                : "Extract key facts from high significance records"
            }
          >
            <button
              type="button"
              className="btn-subtle"
              onClick={refresh}
              disabled={refreshing}
            >
              {refreshing ? "Refreshing…" : hasFacts ? "Refresh" : "Generate"}
            </button>
          </Tooltip>
        </div>
      </header>

      {open && (
        <div className="key-facts-body">
          {loading && (
            <div className="muted" role="status" aria-live="polite">
              Loading…
            </div>
          )}
          {refreshing && !loading && (
            <div
              className="muted"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              Refreshing key facts…
            </div>
          )}
          {error && <div className="form-error">{error}</div>}
          {!loading && !hasFacts && !error && (
            <p className="muted">
              Click Generate to extract key facts from high significance
              records.
            </p>
          )}
          {hasFacts && (
            <>
              <ul className="key-facts-list">
                {facts!.map((f, i) => {
                  const sourceRecord = resolveSourceRecord(f.source, records);
                  return (
                    <li
                      key={`${f.type}-${i}-${f.fact.slice(0, 32)}`}
                      className={`key-fact kf-${f.type}`}
                    >
                      <span className="kf-icon" aria-hidden>
                        {KEY_FACT_EMOJI[f.type]}
                      </span>
                      <span className="kf-type">{f.type}</span>
                      <span className="kf-text">{f.fact}</span>
                      {f.source && sourceRecord && onNavigateToRecord && (
                        <button
                          type="button"
                          className="citation-link kf-source"
                          onClick={() => onNavigateToRecord(sourceRecord.id)}
                          title={`Open source record from ${formatSourceLabel(f.source)}`}
                        >
                          Source: {formatSourceLabel(f.source)}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
              <AiDisclaimer />
            </>
          )}
        </div>
      )}
    </section>
  );
}
